// BUILD-042 Owner-led onboarding — My HR (self), "Documents & Signatures" destination.
// Authority: docs/superpowers/decisions/
//   2026-10-07-build-042-owner-led-onboarding-documents-and-test-roster.md
//
// The Employee's own view: see assigned documents, download/print the blank form, and upload
// a completed scan. Paper print/sign/scan/upload workflow only. Upload alone shows "Awaiting
// HR review" — never "complete" until HR has reviewed it, matching the server-enforced status
// vocabulary exactly.
//
// October 10 12:24 Phoenix addendum (this pass) — same root-cause correction as
// AdminDocumentsSection, applied here for consistency between the two surfaces that render the
// exact same underlying records:
//   - Compact, single-line, EXPANDABLE rows replace the always-expanded card.
//   - THREE groups: "Needs your action" (actionable), "Completed records" (reviewed complete —
//     stays its own visible group, not folded into History), "History" (actually cancelled).
//   - On a completed record, View/Download the COMPLETED COPY (the employee's own uploaded,
//     HR-reviewed scan) is primary; the blank original is a secondary "Original assigned form".
//   - Preview (inline, new tab) vs Download (named save) are two distinct actions.
//   - Dates use the same shared, truthful documentDateLabel.ts as AdminDocumentsSection.
import { useEffect, useState } from 'react';
import { getMyHrDocuments, downloadHrDocumentTemplate, downloadHrDocumentSubmission, uploadHrDocumentSubmission, type HrDocumentAssignment } from '../../../api/client';
import { CardTitle } from './SectionHeading';
import { DocumentUploadAction } from './DocumentUploadAction';
import { checkDownloadUrl } from './documentFileCheck';
import { documentDateLabel } from './documentDateLabel';
import { SigningPanel } from './SigningPanel';

import { AWAITING_HR_REVIEW_EMPLOYEE_LABEL, employeeDocumentAction } from './attentionModel';

const STATUS_LABEL: Record<HrDocumentAssignment['status'], string> = {
  awaiting_return: 'Awaiting your return',
  // Oct 17: submitted/signed and now with HR. Deliberately NOT an action for the employee, and said so in exactly these words.
  awaiting_hr_review: AWAITING_HR_REVIEW_EMPLOYEE_LABEL,
  needs_correction: 'Needs correction — please re-upload',
  reviewed_complete: 'Reviewed complete',
  // October 8 08:19/08:20 addenda: a cancelled assignment is HR's reversal of a mistaken
  // request — nothing further is expected of the employee, shown plainly rather than hidden.
  cancelled: 'Cancelled by HR — no action needed',
};
const STATUS_COLOR: Record<HrDocumentAssignment['status'], string> = {
  awaiting_return: 'text-gray-500 bg-gray-50',
  // Oct 18: neutral. A document with HR is not the employee's action and must not look like one.
  awaiting_hr_review: 'text-gray-600 bg-gray-100',
  needs_correction: 'text-red-700 bg-red-50',
  reviewed_complete: 'text-green-700 bg-green-50',
  cancelled: 'text-gray-400 bg-gray-50',
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data: URL prefix — the backend expects raw base64 only.
      resolve(result.split(',')[1] ?? '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function DocumentsSelfSection({ focusAssignmentId }: { focusAssignmentId?: string | null } = {}) {
  const [assignments, setAssignments] = useState<HrDocumentAssignment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signedDate, setSignedDate] = useState<Record<string, string>>({});
  const [downloadNotice, setDownloadNotice] = useState<Record<string, string>>({});
  const [historyOpen, setHistoryOpen] = useState(false);
  const [completedOpen, setCompletedOpen] = useState(true);
  const [uploadNotice, setUploadNotice] = useState<string | null>(null);
  // October 10 12:24 addendum: compact, EXPANDABLE rows.
  // A link from the attention list opens that document's row directly.
  const [expandedId, setExpandedId] = useState<string | null>(focusAssignmentId ?? null);
  // October 10 18:22 addendum root-cause fix (same pattern as AdminDocumentsSection): a known
  // invalid legacy sample shows "unavailable" the moment a row is expanded, never a
  // working-looking button discovered broken only after a click.
  const [fileValidity, setFileValidity] = useState<Record<string, { valid: boolean; reason?: string }>>({});

  function refresh() {
    getMyHrDocuments()
      .then((d) => { setAssignments(d.assignments); setError(null); })
      .catch((e: any) => setError(e.message || 'Could not load your documents.'));
  }
  useEffect(() => { refresh(); }, []);

  // October 10 12:24 addendum: Preview (inline, new tab) and Download (attachment, named save)
  // are two distinct actions against the same file.
  async function handleOpen(key: string, getUrl: (disposition: 'inline' | 'attachment') => Promise<{ url: string; fileName: string }>, disposition: 'inline' | 'attachment') {
    setDownloadNotice((m) => ({ ...m, [key]: disposition === 'inline' ? 'Checking file…' : 'Preparing download…' }));
    try {
      const { url, fileName } = await getUrl(disposition);
      const result = await checkDownloadUrl(url);
      if (!result.looksValid) {
        setDownloadNotice((m) => ({ ...m, [key]: `"${fileName}" could not be opened: ${result.reason}` }));
        return;
      }
      setDownloadNotice((m) => ({ ...m, [key]: '' }));
      window.open(url, '_blank', 'noopener');
    } catch (e: any) {
      setDownloadNotice((m) => ({ ...m, [key]: e.message || 'Failed to prepare the file.' }));
    }
  }

  // October 10 18:22 addendum — same proactive check as AdminDocumentsSection, applied to the
  // employee's own primary file (completed copy if one exists, otherwise the blank form).
  async function checkPrimaryFile(
    a: HrDocumentAssignment,
    currentSubmission: HrDocumentAssignment['submissions'][number] | undefined,
    submissionKey: string | null,
    templateKey: string,
    hasCompletedCopy: unknown
  ) {
    const key = hasCompletedCopy ? submissionKey! : templateKey;
    if (fileValidity[key]) return;
    try {
      const { url } = hasCompletedCopy
        ? await downloadHrDocumentSubmission(currentSubmission!.submissionId, 'inline')
        : await downloadHrDocumentTemplate(a.assignmentId, 'inline');
      const result = await checkDownloadUrl(url);
      setFileValidity((m) => ({ ...m, [key]: { valid: result.looksValid, reason: result.reason } }));
    } catch (e: any) {
      setFileValidity((m) => ({ ...m, [key]: { valid: false, reason: e.message || 'Could not check this file.' } }));
    }
  }

  async function handleUpload(assignmentId: string, file: File) {
    const fileContentBase64 = await fileToBase64(file);
    const contentType = file.type || 'application/pdf';
    await uploadHrDocumentSubmission(assignmentId, {
      fileName: file.name, fileContentBase64, contentType,
      reportedSignedDate: signedDate[assignmentId] || undefined,
    });
    // Same fix as AdminDocumentsSection: the status change unmounts DocumentUploadAction
    // before its own inline success message is seen — surface it at the section level, which
    // survives the status change.
    setUploadNotice(`Uploaded "${file.name}". ${AWAITING_HR_REVIEW_EMPLOYEE_LABEL}.`);
    refresh();
  }

  function renderRow(a: HrDocumentAssignment) {
    const currentSubmission = a.submissions[0];
    const templateKey = `template-${a.assignmentId}`;
    const submissionKey = currentSubmission ? `submission-${currentSubmission.submissionId}` : null;
    const { label: dateLabel, dateText } = documentDateLabel(a, currentSubmission);
    const expanded = expandedId === a.assignmentId;
    // An electronically signed equipment form is already the real, retained signed copy while HR is still reviewing it, so it is
    // offered right away (labelled as awaiting review). Paper uploads keep the existing rule: only HR-approved copies are primary.
    const isEquipment = !!a.equipmentCheckout;
    // Oct 15 22:12: an ordinary uploaded-PDF form with a pinned signing setup is e-signable exactly like an equipment form.
    const isESign = isEquipment || !!a.documentSigning;
    const eSignedAwaitingReview = a.status === 'awaiting_hr_review' && isESign;
    const hasCompletedCopy = (a.status === 'reviewed_complete' || eSignedAwaitingReview) && currentSubmission && submissionKey;
    // An e-signable form that should have a signed copy but does not: say so. The unsigned file is NEVER offered in its place.
    const equipmentSignedMissing = isESign && (a.status === 'awaiting_hr_review' || a.status === 'reviewed_complete') && !currentSubmission;
    const formNoun = isEquipment ? 'Your equipment form' : 'Your form';
    // October 10 18:22 addendum: an archived (moved-to-History) record is still technically
    // status='reviewed_complete' — show a distinct "Archived" badge, matching
    // AdminDocumentsSection, so it never looks identical to a current completed record.
    const isArchived = a.status === 'reviewed_complete' && !!a.movedToHistoryAt;
    // Oct 18: ONE specific yellow status for what the employee must do (Signature needed / Upload needed / Correction needed), computed by the
    // same rule the server uses for the count; nothing else on this row says "Pending". An e-signed form the provider has not confirmed yet is
    // neutral ("Signed - confirming"), because the employee has nothing left to do.
    const action = employeeDocumentAction(a);
    const signedConfirming = !action && !isArchived && (a.status === 'awaiting_return' || a.status === 'needs_correction') && !a.cancelledAt;
    const badgeLabel = isArchived ? 'Archived' : action ? action.label : signedConfirming ? 'Signed — confirming with the signing service' : STATUS_LABEL[a.status];
    const badgeColor = isArchived ? 'text-gray-600 bg-gray-100'
      : action ? 'text-amber-900 bg-amber-100 border border-amber-300 font-semibold'
      : signedConfirming ? 'text-gray-600 bg-gray-100'
      : STATUS_COLOR[a.status];
    const actionButtonLabel = action ? (action.kind === 'signature' ? 'Review & sign' : action.kind === 'correction' ? 'Correct & resubmit' : 'Upload signed copy') : null;

    return (
      <li key={a.assignmentId} className="py-2.5">
        <button
          type="button"
          onClick={() => {
            const next = expanded ? null : a.assignmentId;
            setExpandedId(next);
            if (next) checkPrimaryFile(a, currentSubmission, submissionKey, templateKey, hasCompletedCopy);
          }}
          className="w-full flex items-center justify-between gap-3 flex-wrap text-left"
          aria-expanded={expanded}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-gray-400 text-xs flex-shrink-0">{expanded ? '▾' : '▸'}</span>
            <span className="text-sm font-semibold text-gray-900 truncate">{a.templateName}</span>
            <span className="text-xs text-gray-400 flex-shrink-0">v{a.versionNumber}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-gray-400">{dateLabel} {dateText}</span>
            <span className={`text-xs font-medium px-2 py-1 rounded-md ${badgeColor}`} data-testid={action ? 'row-status-action' : 'row-status'} data-status={a.status}>{badgeLabel}</span>
          </div>
        </button>
        {/* Oct 18: a visible action straight to the EXISTING signing / upload flow (opens this row, where Review & sign / Upload live). */}
        {action && !expanded && actionButtonLabel && (
          <div className="mt-1.5 pl-5">
            <button
              type="button"
              onClick={() => { setExpandedId(a.assignmentId); checkPrimaryFile(a, currentSubmission, submissionKey, templateKey, hasCompletedCopy); }}
              className="text-xs font-semibold text-[#176A9B] border border-[#4AABDE] rounded-md px-2.5 py-1 hover:bg-[#EDF5FC]"
              data-testid="row-action"
            >
              {actionButtonLabel} →
            </button>
          </div>
        )}

        {expanded && (
          <div className="mt-2 pl-5">
            {/* October 10 18:22 addendum: the completed copy is the ONLY primary action on a
                completed/archived record — the original blank-form buttons no longer appear
                in the routine view (they move under collapsed "Record details" below). A
                pending/awaiting assignment still shows the blank form directly. */}
            {(() => {
              const primaryKey = hasCompletedCopy ? submissionKey! : templateKey;
              const primaryValidity = fileValidity[primaryKey];
              const primaryUnavailable = primaryValidity && !primaryValidity.valid;
              return (
                <div className="flex gap-3 text-xs items-center flex-wrap">
                  <span className="text-gray-500">{equipmentSignedMissing ? 'Your signed copy:' : hasCompletedCopy ? (eSignedAwaitingReview ? 'Your signed copy (awaiting HR review):' : 'Your completed copy:') : isESign ? (a.status === 'needs_correction' ? `${formNoun} (needs signing again):` : `${formNoun} (not yet signed):`) : 'Blank form:'}</span>
                  {equipmentSignedMissing ? (
                    <span className="text-gray-400 italic" data-testid="signed-copy-unavailable">Unavailable — the signed copy could not be found. Please contact HR.</span>
                  ) : primaryUnavailable ? (
                    <span className="text-gray-400 italic">Unavailable — {primaryValidity!.reason}</span>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => handleOpen(primaryKey, hasCompletedCopy ? (d) => downloadHrDocumentSubmission(currentSubmission!.submissionId, d) : (d) => downloadHrDocumentTemplate(a.assignmentId, d), 'inline')}
                        className="text-[#4AABDE] hover:underline font-medium"
                      >
                        {hasCompletedCopy ? 'View' : 'View / print'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOpen(primaryKey, hasCompletedCopy ? (d) => downloadHrDocumentSubmission(currentSubmission!.submissionId, d) : (d) => downloadHrDocumentTemplate(a.assignmentId, d), 'attachment')}
                        className="text-[#4AABDE] hover:underline font-medium"
                      >
                        Download
                      </button>
                    </>
                  )}
                </div>
              );
            })()}
            {!hasCompletedCopy && downloadNotice[templateKey] && (
              <div className="mt-1 text-xs text-amber-700">{downloadNotice[templateKey]}</div>
            )}
            {isESign && !hasCompletedCopy && !equipmentSignedMissing && (
              <p className="mt-1 text-[11px] text-gray-500" data-testid="original-explainer">
                This opens the blank form exactly as HR sent it. It does not contain your answers or your signature. They are added when you sign it electronically below, and your signed copy appears here afterwards.
              </p>
            )}
            {submissionKey && downloadNotice[submissionKey] && (
              <div className="mt-1 text-xs text-amber-700">{downloadNotice[submissionKey]}</div>
            )}

            {/* October 10 18:22 addendum: the original blank form is retained exactly as it
                was and never deleted — it moves under a collapsed "Record details" reference. */}
            {/* Employees see ONE file for a signed equipment form: the signed copy. The unsigned issued copy is no longer offered
                to them afterwards (HR keeps it under Record details). Every other document keeps its existing Record details. */}
            {hasCompletedCopy && !isESign && (
              <details className="mt-2 text-xs">
                <summary className="text-gray-400 cursor-pointer hover:text-gray-600">Record details</summary>
                <div className="mt-1.5 flex gap-3 items-center flex-wrap pl-3">
                  <span className="text-gray-400">Original assigned form:</span>
                  {fileValidity[templateKey] && !fileValidity[templateKey].valid ? (
                    <span className="text-gray-400 italic">Unavailable — {fileValidity[templateKey].reason}</span>
                  ) : (
                    <>
                      <button type="button" onClick={() => handleOpen(templateKey, (d) => downloadHrDocumentTemplate(a.assignmentId, d), 'inline')} className="text-gray-500 hover:underline">
                        View
                      </button>
                      <button type="button" onClick={() => handleOpen(templateKey, (d) => downloadHrDocumentTemplate(a.assignmentId, d), 'attachment')} className="text-gray-500 hover:underline">
                        Download
                      </button>
                    </>
                  )}
                  {downloadNotice[templateKey] && <span className="text-amber-700">{downloadNotice[templateKey]}</span>}
                </div>
              </details>
            )}

            {/* BUILD-042 equipment checkout: Review & Sign on the existing signature line. The
                print / sign / upload path below remains available as the fallback. */}
            {isESign && (
              <SigningPanel assignment={a} mode="self" callerEmployeeId={a.employeeId} onChanged={refresh} />
            )}

            {(a.status === 'awaiting_return' || a.status === 'needs_correction') && (() => {
              const paper = (
                <div className="mt-2 space-y-1.5">
                  <div className="flex items-center gap-2 text-xs">
                    <label htmlFor={`signed-date-${a.assignmentId}`} className="text-gray-500">Date you signed on paper (optional):</label>
                    <input
                      id={`signed-date-${a.assignmentId}`}
                      type="date"
                      className="border border-gray-300 rounded px-2 py-1 text-xs"
                      value={signedDate[a.assignmentId] ?? ''}
                      onChange={(e) => setSignedDate((m) => ({ ...m, [a.assignmentId]: e.target.value }))}
                    />
                  </div>
                  <div className="text-[11px] text-gray-400">This is just the date you wrote on the paper when you signed it — not an electronic signature or verification.</div>
                  <DocumentUploadAction onUpload={(file) => handleUpload(a.assignmentId, file)} />
                </div>
              );
              // An e-signable form is signed electronically (Review & Sign above); the paper route stays available but folded away.
              return isESign ? (
                <details className="mt-2 text-xs" data-testid="paper-fallback">
                  <summary className="text-gray-400 cursor-pointer hover:text-gray-600">Can't sign electronically? Sign on paper and upload instead</summary>
                  {paper}
                </details>
              ) : paper;
            })()}
            {a.status === 'needs_correction' && a.submissions[0]?.reviewNote && (
              <div className="mt-2 bg-red-50 rounded-md p-3 text-xs text-red-700">
                HR noted: {a.submissions[0].reviewNote}
              </div>
            )}
            {a.status === 'cancelled' && a.cancelReason && (
              <div className="mt-2 bg-gray-50 rounded-md p-3 text-xs text-gray-500">
                HR cancelled this assignment — "{a.cancelReason}"
              </div>
            )}
            {/* October 10 18:22 addendum: an Archived record is distinct from a Cancelled one
                — the employee's completed copy stays reviewed/complete, HR has simply moved
                its placement. Shown plainly; no action is offered here either way. */}
            {isArchived && (
              <div className="mt-2 bg-gray-50 rounded-md p-3 text-xs text-gray-500">
                HR archived this record. Your completed copy above remains exactly as it was reviewed.
              </div>
            )}
            {a.status === 'reviewed_complete' && currentSubmission?.reviewNote && (
              <div className="mt-2 bg-green-50 rounded-md p-3 text-xs text-green-800">
                HR noted: {currentSubmission.reviewNote}
              </div>
            )}
          </div>
        )}
      </li>
    );
  }

  // Oct 17: only documents waiting on the EMPLOYEE are actions. A document already submitted or signed and now waiting on HR is shown
  // in its own group ("Waiting on HR") and never counted as the employee's action (isEmployeeActionStatus mirrors the server's rule).
  // Oct 18: the SAME rule as the server count (employeeDocumentAction): an e-signed form awaiting confirmation is not an action either.
  const actionable = (assignments ?? []).filter((a) => employeeDocumentAction(a) !== null);
  const waitingOnHr = (assignments ?? []).filter((a) => a.status === 'awaiting_hr_review' || ((a.status === 'awaiting_return' || a.status === 'needs_correction') && !a.cancelledAt && employeeDocumentAction(a) === null));
  // October 10 12:24 addendum: "Completed records" is its own group — still available/current,
  // not archived history. October 10 14:05 addendum: a record moved to History by HR is
  // excluded here even though it remains status reviewed_complete — placement is independent
  // of status.
  const completed = (assignments ?? []).filter((a) => a.status === 'reviewed_complete' && !a.movedToHistoryAt);
  // History holds what is ACTUALLY cancelled, or that HR explicitly moved to History. The
  // employee can VIEW/DOWNLOAD their own completed copy here (per the explicit requirement),
  // but has no Move to History/Restore/Retire/Reactivate/Delete action anywhere on this page.
  const history = (assignments ?? []).filter((a) => a.status === 'cancelled' || (a.status === 'reviewed_complete' && a.movedToHistoryAt));

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
      <CardTitle>Your documents</CardTitle>
      {uploadNotice && <div className="text-sm text-green-700 mb-3">{uploadNotice}</div>}
      {error ? (
        <div className="text-sm text-red-700">{error}</div>
      ) : assignments === null ? (
        <div className="text-sm text-gray-400">Loading…</div>
      ) : assignments.length === 0 ? (
        <div className="text-sm text-gray-400 py-2">No documents assigned yet.</div>
      ) : (
        <>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Needs your action {actionable.length > 0 && <span className="text-gray-400">({actionable.length})</span>}
          </h3>
          {actionable.length === 0 ? (
            <div className="text-sm text-gray-400 py-2 mb-2">Nothing needs your action right now.</div>
          ) : (
            <ul className="divide-y divide-gray-100 mb-2">
              {actionable.map(renderRow)}
            </ul>
          )}

          {waitingOnHr.length > 0 && (
            <div className="mt-2" data-testid="waiting-on-hr-group">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Waiting on HR <span className="text-gray-400">({waitingOnHr.length})</span></h3>
              <ul className="divide-y divide-gray-100 mb-2">
                {waitingOnHr.map(renderRow)}
              </ul>
            </div>
          )}

          {completed.length > 0 && (
            <div className="mt-4 border-t border-gray-100 pt-2">
              <button
                type="button"
                onClick={() => setCompletedOpen((v) => !v)}
                className="text-xs font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700"
              >
                Completed records ({completed.length}) {completedOpen ? '▾' : '▸'}
              </button>
              {completedOpen && (
                <ul className="divide-y divide-gray-100 mt-2">
                  {completed.map(renderRow)}
                </ul>
              )}
            </div>
          )}

          {history.length > 0 && (
            <div className="mt-4 border-t border-gray-100 pt-2">
              <button
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
                className="text-xs font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700"
              >
                Archived records ({history.length}) {historyOpen ? '▾' : '▸'}
              </button>
              {historyOpen && (
                <ul className="divide-y divide-gray-100 mt-2">
                  {history.map(renderRow)}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      <details className="text-xs text-gray-500 mt-4">
        <summary className="text-[#4AABDE] cursor-pointer">How this works</summary>
        <div className="mt-2 max-w-lg space-y-1.5">
          <p>Click a row to expand it. View/print the blank form, sign it on paper, then scan or photograph it and upload the completed copy using the "Upload signed copy" button.</p>
          <p>Uploading does not mark it complete — HR reviews every submission before it is marked reviewed complete.</p>
          <p>"Needs your action" shows only documents waiting on you. Once you sign or upload a document it moves to "Waiting on HR" and nothing more is needed from you until HR reviews it. "Completed records" are reviewed and complete, and stay visible here as the current record. "Archived records" holds assignments HR actually cancelled, or that HR archived — your completed copy stays available to view/download there too.</p>
        </div>
      </details>
    </div>
  );
}
